import React, { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Clock, Pill as PillIcon, ShieldAlert, Pencil, History, FileText, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtDate } from "../lib/api";
import { BoxTer } from "./TerapieTabella";
import { apriFile } from "./FileViewer";
import { ModificaDialog } from "./TerapieCartella";
import { ModificaEventoBtn } from "./TerapieEvento";
import { useCartellaData } from "./SchedaPazienteVisite";
import { Card, Tiles, Pill } from "./SmartUI";
import { useAuth } from "../context/AuthContext";
import { useFirma } from "../context/FirmaContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";

const GRUPPI = [["ordinaria", "Terapia ordinaria", "#1f7a5c"], ["bisogno", "Al bisogno", "#b45309"], ["evento", "Evento sanitario", "#be123c"]];
const orariN = (ters) => new Set(ters.flatMap((t) => (t.orari || "").split(",").map((x) => x.trim()).filter(Boolean))).size;

export const useFileTerapie = (ospiteId) => useQuery({ queryKey: ["file-terapie", ospiteId], queryFn: async () => (await api.get("/visite-mediche/file-terapie", { params: { ospite_id: ospiteId } })).data });
const useEvTer = (o) => useQuery({ queryKey: ["scheda-ev-ter", o.structure_id], queryFn: async () => (await api.get("/terapie/evento-attive", { params: { structure_id: o.structure_id } })).data }).data || [];
const attive = (terapie) => (terapie || []).filter((t) => (!t.stato || t.stato === "attiva") && t.status !== "eliminato");

function CaricaRicetta({ o, terapie, open, setOpen }) {
  const qc = useQueryClient();
  const { signerName } = useFirma();
  const evAll = useEvTer(o);
  const fileRef = useRef();
  const [f, setF] = useState({ ambito: "ordinaria", farmaco: "", medico: "" });
  const [busy, setBusy] = useState(false);
  const att = attive(terapie);
  const farmaci = { ordinaria: att.filter((t) => t.tipo !== "bisogno"), bisogno: att.filter((t) => t.tipo === "bisogno"), evento: evAll.filter((t) => t.ospite_id === o.id) }[f.ambito];
  const salva = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Seleziona il file della ricetta"); return; }
    if (!f.farmaco) { toast.error("Scegli la terapia a cui collegare la ricetta"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("ospite_id", o.id); fd.append("ambito", f.ambito); fd.append("farmaco", f.farmaco);
      if (f.medico) fd.append("medico", f.medico);
      if (signerName) fd.append("operatore", signerName);
      await api.post("/visite-mediche/terapie-ricetta", fd);
      toast.success("Ricetta caricata");
      qc.invalidateQueries({ queryKey: ["file-terapie", o.id] });
      setOpen(false); setF({ ambito: "ordinaria", farmaco: "", medico: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel caricamento"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md" data-testid="carica-ricetta-dialog">
        <DialogHeader><DialogTitle>Carica ricetta</DialogTitle><DialogDescription>La ricetta viene salvata in File terapie, collegata alla terapia scelta.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs font-semibold mb-1 block">Tipo di terapia *</Label>
            <div className="flex gap-1.5 flex-wrap">{GRUPPI.map(([k, l]) => <Pill key={k} on={f.ambito === k} onClick={() => setF((p) => ({ ...p, ambito: k, farmaco: "" }))} testid={`ricetta-tipo-${k}`}>{l}</Pill>)}</div>
          </div>
          <div><Label className="text-xs font-semibold mb-1 block">Terapia *</Label>
            <Select value={f.farmaco} onValueChange={(v) => setF((p) => ({ ...p, farmaco: v }))}>
              <SelectTrigger data-testid="ricetta-farmaco"><SelectValue placeholder={farmaci.length ? "Scegli il farmaco" : "Nessuna terapia di questo tipo"} /></SelectTrigger>
              <SelectContent>{[...new Set(farmaci.map((t) => `${t.farmaco}${t.dosaggio ? ` ${t.dosaggio}` : ""}`))].map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs font-semibold mb-1 block">Medico</Label><Input value={f.medico} onChange={(e) => setF((p) => ({ ...p, medico: e.target.value }))} placeholder={o.medico_base || "Es. Dr. Rossi"} data-testid="ricetta-medico" /></div>
          <div><Label className="text-xs font-semibold mb-1 block">File (foto o PDF) *</Label><Input type="file" accept="image/*,application/pdf" ref={fileRef} data-testid="ricetta-file" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)} data-testid="ricetta-annulla">Annulla</Button><Button onClick={salva} disabled={busy} className="font-semibold" data-testid="ricetta-salva">{busy ? "Caricamento…" : "Carica"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ModBtn = ({ ospiteId, ambito, label, cls, testid }) => {
  const { can } = useAuth();
  const data = useCartellaData(ospiteId);
  const [open, setOpen] = useState(false);
  if (!can("terapie_modifica")) return null;
  const bis = ambito === "bisogno";
  const sel = (t) => (bis ? t.tipo === "bisogno" : t.tipo !== "bisogno");
  const prs = (data?.terapie_programmate || []).filter((p) => (bis ? p.ambito === "bisogno" : p.ambito !== "bisogno"));
  return <>
    <button type="button" disabled={!data} onClick={() => setOpen(true)} className={`ml-auto inline-flex items-center gap-1 text-[11px] font-semibold rounded-md border px-2 py-0.5 transition-colors disabled:opacity-50 ${cls}`} data-testid={testid}><Pencil size={11} /> {label}</button>
    {data && open && <ModificaDialog open={open} setOpen={setOpen} ospiteId={ospiteId} ambito={ambito} terapie={data.terapie.filter(sel)} terminate={(data.terapie_terminate || []).filter(sel)} programmate={prs} />}
  </>;
};

export function TerapieSmart({ o, terapie }) {
  const nav = useNavigate();
  const evAll = useEvTer(o);
  const { data: files = [] } = useFileTerapie(o.id);
  const att = attive(terapie);
  const ord = att.filter((t) => t.tipo !== "bisogno");
  const bis = att.filter((t) => t.tipo === "bisogno");
  const ev = evAll.filter((t) => t.ospite_id === o.id);
  const attesa = ev.some((t) => t.da_confermare);
  return (
    <div className="space-y-4" data-testid="scheda-terapie">
      <Tiles testid="terapie-tiles" items={[
        ["Quotidiana", `${ord.length} farmaci`, `${orariN(ord)} orari`, "#1f7a5c"],
        ["Al bisogno", bis.length, bis.length ? "pronte all'uso" : "nessuna", "#b45309"],
        ["Per evento", ev.length, attesa ? "in attesa di ricetta" : ev.length ? "evento in corso" : "nessuna", "#be123c", ev.length > 0],
        ["File terapie", files.length, files[0] ? `ultimo ${fmtDate(files[0].data)}` : "nessun file", "#7c3aed"],
      ]} />
      <Card title="Terapie" testid="terapie-card" extra={(
        <div className="flex gap-2 flex-wrap">
          <button type="button" onClick={() => nav(`/impostazione-terapie?ospite=${o.id}`)} className="h-8 px-3 rounded-lg bg-emerald-800 text-white text-xs font-bold inline-flex items-center gap-1" data-testid="scheda-add-terapia"><Pencil size={13} /> Modifica in Impostazione terapie</button>
          <button type="button" onClick={() => nav(`/terapie/ospite/${o.id}`)} className="h-8 px-3 rounded-lg border bg-white text-xs font-bold inline-flex items-center gap-1" data-testid="scheda-gestisci-terapie"><History size={13} /> Storico</button>
        </div>
      )}>
        <div className="grid grid-cols-1 xl:grid-cols-[1.4fr_1fr] gap-3 items-start">
          <BoxTer titolo="Terapia ordinaria" color="#1f7a5c" Icon={Clock} ters={ord} testid="smart-ter-ord" extra={<ModBtn ospiteId={o.id} ambito="ordinaria" label="Modifica" cls="border-emerald-300 text-emerald-800 hover:bg-emerald-50" testid="smart-ter-ord-modifica" />} />
          <BoxTer titolo="Terapia al bisogno" color="#b45309" Icon={PillIcon} ters={bis} bisogno testid="smart-ter-bis" extra={<ModBtn ospiteId={o.id} ambito="bisogno" label="Modifica" cls="border-amber-300 text-amber-800 hover:bg-amber-50" testid="smart-ter-bis-modifica" />} />
        </div>
        {ev.length > 0 && <div className="mt-3"><BoxTer titolo="Terapia per evento sanitario" color="#dc2626" Icon={ShieldAlert} ters={ev} testid="smart-ter-ev" extra={<span className="ml-auto flex items-center gap-2"><ModificaEventoBtn ospiteId={o.id} structureId={o.structure_id} /><button type="button" onClick={() => nav("/infettive")} className="text-[11px] font-bold text-rose-700 hover:underline" data-testid="smart-ter-ev-link">Vai a Eventi sanitari →</button></span>} /></div>}
      </Card>
    </div>
  );
}

const FileRiga = ({ f, onDel }) => {
  const img = (f.content_type || "").startsWith("image");
  return (
    <div className="flex items-center gap-2 py-2 border-t first:border-0" data-testid={`file-ter-${f.id}`}>
      <button type="button" onClick={() => apriFile(f.url, f.titolo)} className="flex-1 min-w-0 flex items-center gap-3 text-left hover:bg-slate-50 rounded" data-testid={`file-ter-apri-${f.id}`}>
        <span className={`w-9 h-11 rounded-md grid place-items-center text-[10px] font-bold shrink-0 ${img ? "bg-sky-100 text-sky-700" : "bg-red-100 text-red-700"}`}>{img ? "IMG" : "PDF"}</span>
        <div className="flex-1 min-w-0"><b className="text-sm block truncate">{f.titolo}</b><div className="text-[11px] text-slate-500 truncate">{fmtDate(f.data)}{f.medico ? ` · ${f.medico}` : ""}{f.motivo ? ` · ${f.motivo}` : ""}</div></div>
        <span className="text-xs font-bold text-emerald-700 shrink-0">Apri</span>
      </button>
      {onDel && <button type="button" onClick={() => onDel(f)} title="Elimina ricetta" className="h-8 w-8 grid place-items-center rounded-md text-red-600 hover:bg-red-50 shrink-0" data-testid={`file-ter-elimina-${f.id}`}><Trash2 size={15} /></button>}
    </div>
  );
};

export function FileTerapie({ o, terapie }) {
  const ospiteId = o.id;
  const { can } = useAuth();
  const [up, setUp] = useState(false);
  const [del, setDel] = useState(null);
  const qc = useQueryClient();
  const elimina = async () => {
    try { await api.delete(del.del_url); toast.success("Ricetta eliminata"); qc.invalidateQueries({ queryKey: ["file-terapie", ospiteId] }); qc.invalidateQueries({ queryKey: ["vm-ospite", ospiteId] }); setDel(null); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore nell'eliminazione"); }
  };
  const { data: files = [], isLoading } = useFileTerapie(ospiteId);
  const [g, setG] = useState("tutti");
  const vis = GRUPPI.filter(([k]) => g === "tutti" || g === k);
  return (
    <div className="space-y-4" data-testid="file-terapie">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-xs text-slate-500 flex items-center gap-1.5 flex-1 min-w-[220px]"><FileText size={13} /> I file arrivano qui da soli quando viene caricata una ricetta (terapia ordinaria, al bisogno o per evento sanitario).</div>
        {false && can("terapie_modifica") && <Button size="sm" onClick={() => setUp(true)} className="font-semibold" data-testid="file-ter-carica"><Upload size={14} className="mr-1" /> Carica ricetta</Button>}
      </div>
      <CaricaRicetta o={o} terapie={terapie} open={up} setOpen={setUp} />
      <Dialog open={!!del} onOpenChange={(v) => { if (!v) setDel(null); }}>
        <DialogContent className="max-w-md" data-testid="elimina-ricetta-dialog">
          <DialogHeader><DialogTitle>Eliminare la ricetta?</DialogTitle><DialogDescription className="text-foreground">«{del?.titolo}» verrà tolta da File terapie{del?.del_url?.includes("/file/") ? " e dalla visita a cui è allegata" : ""}.</DialogDescription></DialogHeader>
          <DialogFooter className="gap-2"><Button variant="outline" onClick={() => setDel(null)} data-testid="elimina-ricetta-annulla">Annulla</Button><Button variant="destructive" onClick={elimina} data-testid="elimina-ricetta-ok">Elimina</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="flex gap-1.5 flex-wrap" data-testid="file-ter-filtri">
        <Pill on={g === "tutti"} onClick={() => setG("tutti")} testid="file-ter-filtro-tutti">Tutti {files.length}</Pill>
        {GRUPPI.map(([k, l]) => <Pill key={k} on={g === k} onClick={() => setG(k)} testid={`file-ter-filtro-${k}`}>{l} {files.filter((f) => f.gruppo === k).length}</Pill>)}
      </div>
      <div className={`grid grid-cols-1 gap-4 ${vis.length > 1 ? "md:grid-cols-3" : ""}`}>
        {vis.map(([k, l, c]) => {
          const list = files.filter((f) => f.gruppo === k);
          return (
            <Card key={k} title={`${l} · ${list.length}`} color={c} testid={`file-ter-gruppo-${k}`}>
              {isLoading ? <p className="text-sm text-muted-foreground">Caricamento…</p> : !list.length ? <p className="text-sm text-muted-foreground italic">Nessun file.</p> : list.map((f) => <FileRiga key={f.id} f={f} onDel={can("terapie_modifica") ? setDel : null} />)}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
